import BybPerformanceChip from "@Components/common/byb-ds/byb-performance-chip";
import BybTrendChip from "@Components/common/byb-ds/byb-trend-chip";
import { BybVrMenuItem, BybVrSelect } from "@Components/game/common/byb-vr-select";
import BybVrDialog from "@Components/game/common/dialog/byb-vr-dialog";
import formatMetricScoreType from "@Containers/app/stat-cards/tooltips/format-metric-type";
import ProgressChart from "@Containers/shared/cards/progress-chart";
import { calculateAveragePercentageChange } from "@Containers/shared/helpers";
import { getLatestDefinedScore } from "@Containers/shared/progress/utils";
import { AppContext } from "@Context/common/general/context";
import { useProgress } from "@Context/game/progress/progress-context";
import { DateRange, MetricEventTypes } from "@Enums";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { getImprovementColor, getPerformanceColor, getPerformanceFeedbackMessage, isAccountOlderThanTwoMonths } from "@Helpers/feedback";
import { MIN_BYB_IQ_FOR_PERFORMANCE_PHASE } from "@hooks/game/progress/use-progress-data";
import useI18n from "@i18n";
import { Fade, FormControl, Stack, Typography, useTheme } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";

const metricFilterToMetricEventTypes: Partial<Record<MetricFilterTypes, MetricEventTypes>> = {
  [MetricFilterTypes.SCAN_FREQUENCY]: MetricEventTypes.SCAN_FREQUENCY,
  [MetricFilterTypes.SCAN_TIMING]: MetricEventTypes.SCAN_TIMING,
  [MetricFilterTypes.CRITICAL_SCAN]: MetricEventTypes.CRITICAL_SCAN,
  [MetricFilterTypes.AWARENESS]: MetricEventTypes.AWARENESS,
  [MetricFilterTypes.DECISION]: MetricEventTypes.DECISION,
};

export interface IProgressDialogMetrics {
  score: number;
  previousScore: number;
  formattedScore: number;
  unit?: string;
  percentageChange: number;
}

export interface IProgressDialogChart {
  data: Array<{ name: string; score: number | null }>;
  color: string;
}

export interface IProgressDialogContent {
  title: string;
  explanationText: string;
  feedbackMessage?: string;
  improvementColor: string;
  percentageChangeColor: string;
}

export interface IProgressDialog {
  open: boolean;
  onClose: () => void;
  currentMetricKey: MetricFilterTypes;
  onMetricChange?: (metricKey: MetricFilterTypes) => void;
  showDateRangeSelector?: boolean;
}

const ProgressDialog = ({ open, onClose, currentMetricKey, onMetricChange, showDateRangeSelector = true }: IProgressDialog) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const { dateRange, setDateRange, longTermResults, longTermMetricResults, currentBybIq, availableMetrics } = useProgress();
  const { state } = useContext(AppContext);

  const dialogChartHeight = parseInt(theme.spacing(40));

  const metricsList = availableMetrics || [];
  const currentIndex = metricsList.findIndex(metric => metric.key === currentMetricKey);
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < metricsList.length - 1;

  const [displayMetricKey, setDisplayMetricKey] = useState(currentMetricKey);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (currentMetricKey !== displayMetricKey) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayMetricKey(currentMetricKey);
        setIsTransitioning(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [currentMetricKey, displayMetricKey]);

  const { metrics, chart, content } = useMemo(() => {
    const isBybIq = displayMetricKey === MetricFilterTypes.BYB_IQ;
    const metricEventType = metricFilterToMetricEventTypes[displayMetricKey];
    let data;

    if (isBybIq) {
      data = longTermResults;
    } else {
      data = metricEventType ? longTermMetricResults?.[metricEventType]: [];
    }

    const creationTimestamp = state.user.creationDate instanceof Date ? state.user.creationDate.getTime() : state.user.creationDate;
    const isOldAccount = isAccountOlderThanTwoMonths(creationTimestamp);
    const currentScore = getLatestDefinedScore(data || []) || 0;
    const formattedScoreValue = currentScore;

    const percentageChange = isBybIq
      ? (() => {
          const nonNullResults = data?.filter((result: any) => result.score !== null && result.score !== undefined) || [];
          if (nonNullResults.length === 0) return 0;
          const avgFromPeriod = nonNullResults.reduce((acc: number, result: any) => acc + (result.score || 0), 0) / nonNullResults.length;
          if (avgFromPeriod === 0) return 0;
          const diff = ((currentBybIq - avgFromPeriod) / avgFromPeriod) * 100;
          return Math.round(diff);
        })()
      : calculateAveragePercentageChange(data || []);
    const improvementColor = getImprovementColor(percentageChange, theme);

    const bybIqValue = currentBybIq ?? 0;
    let formattedScore = 0;
    let unit = "";

    if (isBybIq) {
      formattedScore = bybIqValue;
      unit = i18n("metric.byb_iq.name");
    } else {
      formattedScore = Number(formatMetricScoreType(displayMetricKey, formattedScoreValue));
      unit = i18n(`metric.${displayMetricKey}.long`);
    }

    const chartColor = getPerformanceColor(displayMetricKey, formattedScore, theme, creationTimestamp);
    const feedbackMessage = i18n(getPerformanceFeedbackMessage(displayMetricKey, formattedScore, currentBybIq < MIN_BYB_IQ_FOR_PERFORMANCE_PHASE));
    const title = isBybIq ? i18n("progress.section.byb_iq") : i18n(`metric.${displayMetricKey.toLowerCase()}.name`);
    const explanationText = isBybIq ? i18n("progress.byb_iq.explanation") : i18n(`metric.${displayMetricKey.toLowerCase()}.explanation`);

    const nonNullResults = data?.filter((result: any) => result.score !== null && result.score !== undefined) || [];
    const avgFromPeriod =
      nonNullResults.length > 0 ? nonNullResults.reduce((acc: number, result: any) => acc + (result.score || 0), 0) / nonNullResults.length : 0;

    return {
      metrics: {
        score: isBybIq ? bybIqValue : currentScore,
        previousScore: avgFromPeriod,
        formattedScore,
        unit,
        percentageChange,
      },
      chart: {
        data: data,
        color: chartColor,
      },
      content: {
        title,
        explanationText,
        feedbackMessage,
        improvementColor,
        percentageChangeColor: improvementColor,
      },
    };
  }, [displayMetricKey, longTermResults, longTermMetricResults, state.user.creationDate, theme, i18n, currentBybIq, currentMetricKey]);

  const handlePrevious = () => {
    if (!canGoPrevious || !onMetricChange) return;
    const previousMetric = metricsList[currentIndex - 1];
    onMetricChange(previousMetric.key as MetricFilterTypes);
  };

  const handleNext = () => {
    if (!canGoNext || !onMetricChange) return;
    const nextMetric = metricsList[currentIndex + 1];
    onMetricChange(nextMetric.key as MetricFilterTypes);
  };

  return (
    <BybVrDialog
      open={open}
      onClose={onClose}
      showCloseButton
      maxWidth="md"
      fullWidth
      name={displayMetricKey}
      onPrevious={handlePrevious}
      onNext={handleNext}
      canGoPrevious={canGoPrevious}
      canGoNext={canGoNext}
    >
      <Stack gap={2} padding={2}>
        <Fade in={!isTransitioning} timeout={300}>
          <div>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="text2xl500" color="dsNeutral.v300">
                {content.title}
              </Typography>
              {showDateRangeSelector && (
                <FormControl>
                  <BybVrSelect value={dateRange} onChange={(event: any) => setDateRange(event.target.value as DateRange)}>
                    <BybVrMenuItem value={DateRange.ALL_TIME}>{i18n("progress.section.progress.all_time")}</BybVrMenuItem>
                    <BybVrMenuItem value={DateRange.PAST_YEAR}>{i18n("progress.section.progress.past_year")}</BybVrMenuItem>
                    <BybVrMenuItem value={DateRange.PAST_3_MONTHS}>{i18n("progress.section.progress.past_3_months")}</BybVrMenuItem>
                    <BybVrMenuItem value={DateRange.PAST_MONTH}>{i18n("progress.section.progress.past_month")}</BybVrMenuItem>
                    <BybVrMenuItem value={DateRange.PAST_14_DAYS}>{i18n("progress.section.progress.past_14_days")}</BybVrMenuItem>
                  </BybVrSelect>
                </FormControl>
              )}
            </Stack>
          </div>
        </Fade>

        <Fade in={!isTransitioning} timeout={300}>
          <Stack gap={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Stack direction="row" alignItems="baseline" gap={1}>
                <Typography variant="text6xl700" color="dsBase.v200">
                  {metrics.formattedScore}
                </Typography>
                {metrics.unit && (
                  <Typography variant="text1xl500" color="dsNeutral.v300">
                    {metrics.unit}
                  </Typography>
                )}
              </Stack>
              {metrics.previousScore !== 0 && (
                <Typography variant="text1xl500" color={content.percentageChangeColor}>
                  {metrics.percentageChange > 0 ? "+" : ""}
                  {metrics.percentageChange}%
                </Typography>
              )}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center">
                <BybPerformanceChip score={metrics.score} metricKey={displayMetricKey as MetricFilterTypes} />
              </Stack>
              <Stack direction="row" alignItems="center">
                <BybTrendChip improvementPercentage={metrics.percentageChange} />
              </Stack>
            </Stack>
          </Stack>
        </Fade>

        <Fade in={!isTransitioning} timeout={300}>
          <div>
            {chart.data && chart.data.length > 0 && (
              <ProgressChart data={chart.data} color={chart.color} height={dialogChartHeight} chartId={displayMetricKey} showAverageScore showUnit />
            )}
          </div>
        </Fade>

        <Fade in={!isTransitioning} timeout={300}>
          <Stack
            width="100%"
            maxWidth={theme.spacing(100)}
            bgcolor={theme.palette.dsSurface.v100}
            padding={4}
            borderRadius={2}
            boxSizing="border-box"
            gap={2}
          >
            {content.feedbackMessage && (
              <Stack width="100%" bgcolor={`${chart.color}08`} padding={2} borderRadius={2} boxSizing="border-box">
                <Typography variant="textBase500" textAlign="center" sx={{ color: chart.color }}>
                  {content.feedbackMessage}
                </Typography>
              </Stack>
            )}
            <Typography variant="textLg400" color="text.secondary" textAlign="justify">
              {content.explanationText}
            </Typography>
          </Stack>
        </Fade>
      </Stack>
    </BybVrDialog>
  );
};

export default ProgressDialog;
